export const contactEmail = 'admin@ehongahara.com';
export const contactLabel = 'admin [at] ehongahara.com';

export const tagPresetsPath = '/tag-presets';

export const discoveryLinks = [
  { label: '3-4歳', tag: '3-4歳' },
  { label: '5-6歳', tag: '5-6歳' },
  { label: '小学生', tag: '小学生' },
  { label: '英語絵本', tag: '英語絵本' },
  { label: '家庭学習', tag: '家庭学習' },
  { label: '図書館本', tag: '図書館本' },
  { label: '読み聞かせ', tag: '読み聞かせ' },
  { label: '娘のお気に入り', tag: '娘のお気に入り' },
];

export function searchPath(query) {
  return `/articles/search?SearchableText=${encodeURIComponent(query)}`;
}

export function tagSearchPath(tag) {
  return `/articles/search?Subject=${encodeURIComponent(tag)}`;
}

export function discoveryLinksFromText(text) {
  const seen = new Set();
  return (text || '')
    .split(/\r?\n|、|,|，|;|；|\s+/)
    .map((tag) => tag.replace(/^#+/, '').trim())
    .filter(Boolean)
    .filter((tag) => {
      if (seen.has(tag)) return false;
      seen.add(tag);
      return true;
    })
    .map((tag) => ({ label: tag, tag }));
}

export function normalizeInternalUrl(url) {
  if (!url) return '/';

  const withoutOrigin = url.replace(/^https?:\/\/[^/]+/i, '');
  const normalized = withoutOrigin
    .replace(/^\/?:8080(?=\/|$)/, '')
    .replace(/\/:8080(?=\/|$)/g, '');

  return normalized || '/';
}

export function itemUrl(item) {
  return normalizeInternalUrl(item?.['@id'] || item?.url || item?.getURL || '');
}

export function itemTags(item) {
  return (item?.Subject || item?.subjects || item?.tags || []).filter(
    (tag) => tag !== '0-2歳',
  );
}

const iconQueryByTag = {
  '3-4歳': 'preschool children books',
  '5-6歳': 'kindergarten reading books',
  小学生: 'elementary school reading',
  英語絵本: 'english picture book',
  家庭学習: 'child studying at home',
  図書館本: 'children library books',
  読み聞かせ: 'parent reading child book',
  '娘のお気に入り': 'child favorite book',
  絵本: 'picture books',
  児童書: 'children literature',
  そろばん: 'abacus learning',
};

export function iconSearchQuery(item) {
  const tags = itemTags(item);
  const tagQuery = tags.map((tag) => iconQueryByTag[tag]).find(Boolean);
  if (tagQuery) return tagQuery;

  const title = item?.title || item?.Title || '';
  if (/英語|ORT|English/i.test(title)) return iconQueryByTag.英語絵本;
  if (/図書館/.test(title)) return iconQueryByTag.図書館本;
  if (/そろばん/.test(title)) return iconQueryByTag.そろばん;
  if (/小学生|小１|小２|小学校/.test(title)) return iconQueryByTag.小学生;
  if (/読み聞かせ|読んだ|絵本|本/.test(title)) return 'children reading picture book';

  return 'children books reading';
}

export const googleBooksSearchUrl = (title) =>
  `https://www.google.com/search?tbm=bks&q=${encodeURIComponent(title)}`;

export const yomerebaSearchUrl = (title) =>
  `https://yomereba.com/search.php?q=${encodeURIComponent(title)}`;

export const googleBooksApiUrl = (title) =>
  `https://www.googleapis.com/books/v1/volumes?q=${encodeURIComponent(
    `intitle:"${title}"`,
  )}&langRestrict=ja&printType=books&maxResults=1`;

export const normalizeImageUrl = (url) =>
  url ? url.replace(/^http:/, 'https:') : '';

export async function lookupBookCover(title) {
  const response = await fetch(googleBooksApiUrl(title));
  if (!response.ok) throw new Error('Google Books lookup failed');
  const data = await response.json();
  const volume = data.items?.[0]?.volumeInfo;

  return {
    title,
    matchedTitle: volume?.title || title,
    authors: volume?.authors || [],
    publisher: volume?.publisher || '',
    image:
      normalizeImageUrl(volume?.imageLinks?.thumbnail) ||
      normalizeImageUrl(volume?.imageLinks?.smallThumbnail),
    link: yomerebaSearchUrl(volume?.title || title),
  };
}

export function formatDate(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  return new Intl.DateTimeFormat('ja-JP', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date);
}

export function plainTextFromBlocks(content, maxLength = 120) {
  const text = firstMeaningfulTextFromBlocks(content) || fullTextFromBlocks(content);

  if (!text) return '';
  return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
}

function textFromSlateValue(value) {
  if (!Array.isArray(value)) return '';

  return value
    .map((node) => {
      if (typeof node?.text === 'string') return node.text;
      return textFromSlateValue(node?.children);
    })
    .filter(Boolean)
    .join(' ');
}

function textFromBlock(block) {
  if (!block) return '';
  if (typeof block.plaintext === 'string' && block.plaintext.trim()) {
    return block.plaintext.trim();
  }
  if (block['@type'] === 'slate') {
    return textFromSlateValue(block.value).trim();
  }
  return '';
}

function isUsefulExcerpt(text) {
  const normalized = text.trim();
  if (normalized.length < 16) return false;
  if (/^(お久しぶりです|こんにちは|はじめまして|以下、?ネタバレあります。?)/.test(normalized)) {
    return false;
  }
  return true;
}

export function firstMeaningfulTextFromBlocks(content) {
  const blockIds = content?.blocks_layout?.items || [];
  return (
    blockIds
      .map((id) => textFromBlock(content.blocks?.[id]))
      .find((text) => isUsefulExcerpt(text)) || ''
  );
}

export function fullTextFromBlocks(content) {
  const blockIds = content?.blocks_layout?.items || [];
  return blockIds
    .map((id) => content.blocks?.[id])
    .map(textFromBlock)
    .filter(Boolean)
    .join(' ');
}

export function editableDiscoveryLinks(content) {
  const fromDescription = discoveryLinksFromText(content?.description);
  if (fromDescription.length) return fromDescription;

  const fromBlocks = discoveryLinksFromText(fullTextFromBlocks(content));
  return fromBlocks.length ? fromBlocks : discoveryLinks;
}

export function extractBookTitles(content, limit = 2) {
  const source = [
    content?.title,
    ...(content?.blocks_layout?.items || [])
      .map((id) => content.blocks?.[id]?.plaintext)
      .filter(Boolean)
      .slice(0, 8),
  ]
    .filter(Boolean)
    .join(' ');

  const titles = [];
  const patterns = [/『([^』]{2,80})』/g, /"([^"]{2,80})"/g, /“([^”]{2,80})”/g];

  patterns.forEach((pattern) => {
    for (const match of source.matchAll(pattern)) {
      const title = match[1].trim();
      if (title && !titles.includes(title)) {
        titles.push(title);
      }
    }
  });

  return titles.slice(0, limit);
}

export function manualBookTitles(content, limit = 2) {
  const rawTitles = content?.book_titles || content?.bookTitles || [];
  const titles = Array.isArray(rawTitles) ? rawTitles : [rawTitles];
  return titles
    .map((title) => (typeof title === 'string' ? title.trim() : ''))
    .filter(Boolean)
    .slice(0, limit);
}

export async function identifyBookMentions(content, limit = 2) {
  const response = await fetch('/++api++/@book-mentions', {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      title: content?.title || '',
      text: fullTextFromBlocks(content),
      limit,
    }),
  });

  if (!response.ok) throw new Error('Book mention lookup failed');
  const data = await response.json();
  return (data.books || []).map((book) => book.title).filter(Boolean);
}

export function itemDate(item) {
  return item.effective || item.created || item.CreationDate || null;
}

export function isBlogArticle(item) {
  const id = item?.id || item?.getId || '';
  const title = item?.title || item?.Title || '';
  const path = item?.['@id'] || item?.url || item?.getURL || item?.getPath || '';
  const identity = `${id} ${title} ${path}`.toLowerCase();

  if (identity.includes('published-images')) return false;
  if (/\/articles\/images(\/|$)/.test(identity)) return false;
  if (item?.portal_type && item.portal_type !== 'Document') return false;
  if (item?.['@type'] && item['@type'] !== 'Document') return false;

  return true;
}

export function isPublishedNow(item) {
  if (!isBlogArticle(item)) return false;

  const effective = item?.effective || item?.EffectiveDate || null;
  if (!effective) return false;

  const effectiveDate = new Date(effective);
  if (Number.isNaN(effectiveDate.getTime())) return false;

  return effectiveDate.getTime() <= Date.now();
}
